import { useMutation } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { adoptSession, type SessionPayload } from '@/lib/session-adopt';
import { useAuthStore, type AuthenticatedUser } from '@/stores/auth-store';

/**
 * The auth endpoints, as mutations.
 *
 * Every one that establishes a session pushes the returned user into the auth store,
 * so the shell renders from one source rather than each page carrying its own copy of
 * "who is signed in".
 */

const withoutChurch = { withoutChurch: true } as const;

export interface SignInInput {
  email: string;
  password: string;
  totpCode?: string;
  rememberMe?: boolean;
}

export const useSignIn = () =>
  useMutation({
    mutationFn: (input: SignInInput) =>
      api.post<SessionPayload>('/auth/login', input, withoutChurch),
    onSuccess: (payload) => {
      adoptSession(payload);
    },
  });

export interface RegisterInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  locale?: string;
  timezone?: string;
  inviteToken?: string;
  acceptedTerms: true;
}

export const useRegister = () =>
  useMutation({
    mutationFn: (input: RegisterInput) =>
      api.post<SessionPayload>('/auth/register', input, withoutChurch),
    onSuccess: (payload) => {
      adoptSession(payload);
    },
  });

/**
 * Succeeds whether or not the address has an account, which is deliberate: the screen
 * must not become a way to find out who is registered.
 */
export const useRequestPasswordReset = () =>
  useMutation({
    mutationFn: (email: string) =>
      api.post<{ accepted: true }>('/auth/request-password-reset', { email }, withoutChurch),
  });

export const useResetPassword = () =>
  useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      api.post<SessionPayload>('/auth/reset-password', input, withoutChurch),
    onSuccess: (payload) => {
      adoptSession(payload);
    },
  });

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export const useChangePassword = () =>
  useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      api.post<{ changed: true }>('/auth/change-password', input, withoutChurch),
  });

export const useVerifyEmail = () =>
  useMutation({
    mutationFn: (token: string) =>
      api.post<AuthenticatedUser>('/auth/verify-email', { token }, withoutChurch),
    onSuccess: (user) => {
      // Not a new session, only a changed field on the current one.
      useAuthStore.getState().setUser(user);
    },
  });

export const useResendVerification = () =>
  useMutation({
    mutationFn: (email: string) =>
      api.post<{ accepted: true }>('/auth/resend-verification', { email }, withoutChurch),
  });
